/* @flow */
import moment from 'moment-timezone';
import { Map, List, fromJS, Record } from 'immutable';
import { isMapLike, isListLike } from 'trustar/utils';
import { map } from 'ramda';
import { IO } from 'trustar/fp';
import type { Action } from 'trustar/utils/redux-types.js';
import createDebugger from 'debug';
const debug = createDebugger('app:modules:create:actions'); // eslint-disable-line no-unused-vars

import * as Profile from 'modules/profile';
import {
  JAVA_DATE_FORMAT,
  MAX_SUBJECT_LENGTH,
} from './constants.js';

/* Map a string of attributes to { name } attribute objects. */
const mapAttributes = (attributes: Array<string>): List<*> =>
  IO.of(attributes)
    .map(map(v => ({ name: v }))) // The format the server wants
    .map(arr => fromJS(arr))
    .foldOr(err => fromJS([])); // Default to list

const mapEnclaves = (report: Map<string, *>, getState: Function): List<*> | Array<*> => {
  const userEnclaves = Profile.getters.enclaves(getState());
  // TODO: Should we remove this piece of code?
  if (report.get('distributionType') === 'COMMUNITY') {
    const enclaves = userEnclaves.toList();
    return report.get('id')
      ? enclaves.filter(e => e.get('create') || e.get('update'))
      : enclaves.filter(e => e.get('create'));
  } else {
    return IO.of(report)
      .map(x =>
        x.get('enclaves')
          .map(enclaveId => userEnclaves.get(enclaveId)))
        .foldOr(err => {
          debug('Error getting enclaves for report submission', err);
          return [];
        });
  }
};

/* Map a report to the format the server is expecting for submission. */
type ReportJSON = {
  title: string,
  reportBody: ?string,
  distributionType: string,
  enclaveGuid: string,
  externalTrackingId?: string | null,
  timeBegan: number,
};

/* NOTE: If the user specifies "Time Unknown", or doesn't explicitly specify the time,
 * we default the time to the current moment the report is submitted to Station.
 */
const defaultTime = () => Date.now();

export const toJSON = (report: Map<string, *>, getState?: Function): ReportJSON => ({
  title: report.get('title'),
  tags: report.get('tags', []).map(tag => ({name: tag})),
  reportBody: report.getIn(['content', 'data']),
  distributionType: report.get('distributionType'),
  enclaveGuid: report.get('enclaveGuid'),
  externalTrackingId: report.get('externalTrackingId', null),
  timeBegan: report.get('timeBeganUnknown')
    ? defaultTime()
    : moment
      .tz(
        report.get('timeBegan') || defaultTime(),
        report.get('timeBeganTimezone'))
      .format(JAVA_DATE_FORMAT),
});

/* Validate report settings before submission */
const validateSubmit = (report: Map<any, any>): string => {
  const title = report.get('title').trim();
  const enclaveGuid = report.get('enclaveGuid');
  const distributionType = report.get('distributionType');
  const reportBody = report.getIn(['content', 'data']);

  if (!title) {
    return 'Report must have a title.';
  }

  if (title.length > MAX_SUBJECT_LENGTH) {
    return `Report title must be less than ${MAX_SUBJECT_LENGTH} characters.`;
  }

  if (!reportBody) {
    return 'Report must have a body. Please attach a file or paste text.';
  }

  if (!enclaveGuid && distributionType === 'ENCLAVE') {
    return 'You must select an enclave to submit to.';
  }

  return '';
};

/* Validate report content before moving to 'review' stage */
export const validateReportContent = (report: Map<any, any>): string => {
  const title = report.get('title').trim();
  const reportBody = report.getIn(['content', 'data']);

  if (!title) {
    return 'Report must have a title.';
  }

  if (title.length > MAX_SUBJECT_LENGTH) {
    return `Report title must be less than ${MAX_SUBJECT_LENGTH} characters.`;
  }

  if (!reportBody) {
    return 'Report must have a body. Please attach a file or paste text.';
  }

  return '';
};

export const getValidationError = ({ payload: report }: Action<*>): string => {
  return validateSubmit(report);
};

export const validateReportError = ({ payload: report }: Action<*>): string => {
  return report.get('id')
    ? ''
    : 'Report does not have an id. Cannot be updated.';
};

export const Form = Record({
  currentValues: Map(),
});

export const toForm = (k: string, v: any) => {
  if (k === '') {
    return Form(v);
  } else if (isMapLike(v)) {
    return Map(v);
  } else if (isListLike(v)) {
    return List(v);
  } else {
    return v;
  }
};



// WEBPACK FOOTER //
// ./src/app/modules/create/utils.js